(() => {
  const ROOT_SELECTOR = '[data-pianzi-community-proof]';
  const INITIALIZED = 'data-pianzi-community-proof-initialized';

  class PianziCommunityProof {
    constructor(root) {
      this.root = root;
      this.metrics = Array.from(root.querySelectorAll('[data-pianzi-community-proof-metric]'));
      this.animateNumbers = root.dataset.countAnimation === 'true';
      this.animateRings = root.dataset.ringAnimation === 'true';
      this.reveal = root.dataset.revealAnimation === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.observer = null;
      this.hasPlayed = new WeakSet();
    }

    init() {
      if (!this.metrics.length) return;
      this.root.classList.add('is-js');

      if (this.reducedMotion || !('IntersectionObserver' in window)) {
        this.metrics.forEach((metric) => this.completeMetric(metric));
        return;
      }

      if (this.reveal || this.animateNumbers || this.animateRings) {
        this.prepareMetrics();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.playMetric(entry.target);
              this.observer?.unobserve(entry.target);
            });
          },
          { threshold: 0.24, rootMargin: '0px 0px -8% 0px' }
        );

        this.metrics.forEach((metric) => this.observer.observe(metric));
      } else {
        this.metrics.forEach((metric) => metric.classList.add('is-visible'));
      }
    }

    prepareMetrics() {
      this.metrics.forEach((metric) => {
        const number = metric.querySelector('[data-pianzi-community-proof-number]');
        const ring = metric.querySelector('[data-pianzi-community-proof-ring]');

        if (this.reveal) metric.classList.remove('is-visible');
        else metric.classList.add('is-visible');

        if (this.animateNumbers && number) {
          number.textContent = this.formatNumber(0, number);
        }

        if (this.animateRings && ring) {
          ring.style.setProperty('--pzcp-progress', '0%');
        }
      });
    }

    playMetric(metric) {
      if (this.hasPlayed.has(metric)) return;
      this.hasPlayed.add(metric);
      metric.classList.add('is-visible');

      const number = metric.querySelector('[data-pianzi-community-proof-number]');
      const ring = metric.querySelector('[data-pianzi-community-proof-ring]');
      const finalProgress = Number.parseFloat(metric.style.getPropertyValue('--pzcp-final-progress')) || 0;
      const duration = 1150;
      const start = performance.now();

      const tick = (now) => {
        const elapsed = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - elapsed, 3);

        if (this.animateNumbers && number) {
          const target = Number.parseFloat(number.dataset.target || '0') || 0;
          number.textContent = this.formatNumber(target * eased, number);
        }

        if (this.animateRings && ring) {
          ring.style.setProperty('--pzcp-progress', `${finalProgress * eased}%`);
        }

        if (elapsed < 1) {
          window.requestAnimationFrame(tick);
        } else {
          if (number) {
            const target = Number.parseFloat(number.dataset.target || '0') || 0;
            number.textContent = this.formatNumber(target, number);
          }
          if (ring) ring.style.setProperty('--pzcp-progress', `${finalProgress}%`);
        }
      };

      window.requestAnimationFrame(tick);
    }

    completeMetric(metric) {
      metric.classList.add('is-visible');
      const number = metric.querySelector('[data-pianzi-community-proof-number]');
      const ring = metric.querySelector('[data-pianzi-community-proof-ring]');
      const finalProgress = Number.parseFloat(metric.style.getPropertyValue('--pzcp-final-progress')) || 0;

      if (number) {
        const target = Number.parseFloat(number.dataset.target || '0') || 0;
        number.textContent = this.formatNumber(target, number);
      }
      if (ring) ring.style.setProperty('--pzcp-progress', `${finalProgress}%`);
    }

    formatNumber(value, element) {
      const decimals = Math.max(0, Number.parseInt(element.dataset.decimals || '0', 10));
      const separator = element.dataset.separator === 'true';

      if (!separator) return Number(value).toFixed(decimals);

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    }

    destroy() {
      this.observer?.disconnect();
      this.root.classList.remove('is-js');
    }
  }

  const instances = new WeakMap();

  function initialize(root) {
    if (!root || root.getAttribute(INITIALIZED) === 'true') return;
    root.setAttribute(INITIALIZED, 'true');
    const instance = new PianziCommunityProof(root);
    instances.set(root, instance);
    instance.init();
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  }

  function destroyWithin(scope) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
      root.removeAttribute(INITIALIZED);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
